import {
  type CylinderSurface,
  GEOMETRY_TOLERANCE,
  type PlacementPosition,
  type SizeMm,
  type SurfacePoint,
} from '@repo/api-contracts';
import { type CylinderParams, frameToWarped, warpedToFrame } from '@repo/warp-engine';
import type { PhotoDimensions } from '../types';
import { cylinderParamsOf } from './cylinder-params';

/**
 * 프레임 높이를 계약 허용오차 안에서 벗어난 yMm 을 위·아래 끝으로 붙인다.
 * 엔진은 높이의 1e-9 배만 넘어도 null 이지만 계약(cylinderPlacementIssue)은 1e-6mm 까지 프레임 안으로 본다.
 */
const snapToFrameHeight = (yMm: number, heightMm: number): number => {
  if (yMm < 0 && yMm >= -GEOMETRY_TOLERANCE) return 0;
  if (yMm > heightMm && yMm <= heightMm + GEOMETRY_TOLERANCE) return heightMm;
  return yMm;
};

/** 어댑터 결과(params)를 한 번 만든 뒤 여러 점을 옮길 때 쓰는 cylinderMmToPhoto 본체. */
export const frameToPhoto = (
  params: CylinderParams,
  frame: SizeMm,
  point: PlacementPosition,
): SurfacePoint | null =>
  frameToWarped(params, frame, {
    xMm: point.xMm,
    yMm: snapToFrameHeight(point.yMm, frame.heightMm),
  });

/** 원통 프레임 위 점(mm)을 사진 점(px)으로 보낸다. 뒷면·프레임 밖·엔진 검사 실패는 null. */
export const cylinderMmToPhoto = (
  surface: CylinderSurface,
  photo: PhotoDimensions,
  point: PlacementPosition,
): SurfacePoint | null => frameToPhoto(cylinderParamsOf(surface, photo), surface.realSize, point);

/** 사진 점(px)을 원통 프레임 위 점(mm)으로 되돌린다. 결과는 [0, W]×[0, H] 안, 실루엣 밖은 null. */
export const cylinderPhotoToMm = (
  surface: CylinderSurface,
  photo: PhotoDimensions,
  point: SurfacePoint,
): PlacementPosition | null =>
  warpedToFrame(cylinderParamsOf(surface, photo), surface.realSize, point);

/** 원통 표면의 가운데 열 축척(px/mm). */
export const cylinderCenterScale = (
  surface: CylinderSurface,
  photo: PhotoDimensions,
): SurfacePoint => {
  const { area } = cylinderParamsOf(surface, photo);

  return {
    x: area.width / surface.realSize.widthMm,
    y: area.height / surface.realSize.heightMm,
  };
};
